import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { getIndustrialRiskYieldSummary } from '../services/industrialRiskService';

import { esNumberFormat } from '../../industrialCost/utils/numberFormat';
type YieldSummaryResponse = {
  status?: string;
  config?: {
    period_key?: string | null;
    max_suppliers?: number | null;
  } | null;
  global?: {
    n_lots?: number | null;
    suppliers_considered?: number | null;
    total_kg?: number | null;
    yield_mean?: number | null;
    yield_p10?: number | null;
    yield_p90?: number | null;
    best_supplier_by_yield_mean?: string | null;
    most_stable_supplier?: string | null;
  } | null;
  suppliers?: Array<{
    supplier?: string | null;
    n_lots?: number | null;
    total_kg?: number | null;
    yield_mean?: number | null;
    yield_p10?: number | null;
    yield_p90?: number | null;
    yield_range?: number | null;
    stability_label?: string | null;
  }> | null;
  quality_report?: {
    decision_grade?: boolean | null;
    reasons?: string[] | null;
    note?: string | null;
  } | null;
  notes?: string[] | null;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

function fmtNumber(value?: number | null, digits = 0): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return esNumberFormat({
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

function pct01(value?: number | null, digits = 1): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${esNumberFormat({
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value * 100)}%`;
}

function normalizeResponse(raw: unknown): YieldSummaryResponse {
  if (!isRecord(raw)) return {};

  const configRaw = isRecord(raw.config) ? raw.config : null;
  const globalRaw = isRecord(raw.global) ? raw.global : null;
  const qualityReportRaw = isRecord(raw.quality_report) ? raw.quality_report : null;

  const suppliers = Array.isArray(raw.suppliers)
    ? raw.suppliers
        .filter(isRecord)
        .map((item) => ({
          supplier: asString(item.supplier),
          n_lots: asNumber(item.n_lots),
          total_kg: asNumber(item.total_kg),
          yield_mean: asNumber(item.yield_mean),
          yield_p10: asNumber(item.yield_p10),
          yield_p90: asNumber(item.yield_p90),
          yield_range: asNumber(item.yield_range),
          stability_label: asString(item.stability_label),
        }))
    : [];

  return {
    status: asString(raw.status),
    config: configRaw
      ? {
          period_key: asString(configRaw.period_key),
          max_suppliers: asNumber(configRaw.max_suppliers),
        }
      : undefined,
    global: globalRaw
      ? {
          n_lots: asNumber(globalRaw.n_lots),
          suppliers_considered: asNumber(globalRaw.suppliers_considered),
          total_kg: asNumber(globalRaw.total_kg),
          yield_mean: asNumber(globalRaw.yield_mean),
          yield_p10: asNumber(globalRaw.yield_p10),
          yield_p90: asNumber(globalRaw.yield_p90),
          best_supplier_by_yield_mean: asString(globalRaw.best_supplier_by_yield_mean),
          most_stable_supplier: asString(globalRaw.most_stable_supplier),
        }
      : undefined,
    suppliers,
    quality_report: qualityReportRaw
      ? {
          decision_grade:
            typeof qualityReportRaw.decision_grade === 'boolean' ? qualityReportRaw.decision_grade : undefined,
          reasons: Array.isArray(qualityReportRaw.reasons)
            ? qualityReportRaw.reasons.filter((item): item is string => typeof item === 'string')
            : [],
          note: asString(qualityReportRaw.note),
        }
      : undefined,
    notes: Array.isArray(raw.notes) ? raw.notes.filter((item): item is string => typeof item === 'string') : [],
  };
}

function computeState(data: YieldSummaryResponse): {
  label: string;
  tone: 'success' | 'warning' | 'error' | 'default';
  narrative: string;
} {
  const global = data.global;
  const reasons = data.quality_report?.reasons || [];
  const mean = global?.yield_mean;
  const p10 = global?.yield_p10;
  const p90 = global?.yield_p90;
  const spread = p10 != null && p90 != null ? p90 - p10 : undefined;
  const nLots = global?.n_lots ?? 0;
  const supplierCount = global?.suppliers_considered ?? 0;

  if (!global || nLots <= 0) {
    return {
      label: 'Sin base suficiente',
      tone: 'default',
      narrative: 'Todavía no hay base histórica suficiente para construir una lectura ejecutiva del rendimiento observado.',
    };
  }

  if (nLots < 12 || supplierCount < 2) {
    return {
      label: 'Lectura inicial',
      tone: 'default',
      narrative:
        'Ya existe señal histórica de rendimiento, pero la base todavía es limitada y conviene interpretar esta lectura como una referencia inicial.',
    };
  }

  if (reasons.length > 0 || spread == null) {
    return {
      label: 'Con cautela',
      tone: 'warning',
      narrative:
        'El rendimiento histórico ya aporta señal útil, pero la cobertura disponible todavía exige prudencia al interpretar diferencias entre proveedores.',
    };
  }

  if (spread >= 0.12) {
    return {
      label: 'Variable',
      tone: 'warning',
      narrative:
        'La conversión observada cambia de forma relevante entre lotes y proveedores, por lo que una media anual por sí sola no explica bien el comportamiento real.',
    };
  }

  if (spread >= 0.07) {
    return {
      label: 'Con cautela',
      tone: 'warning',
      narrative:
        'El rendimiento observado presenta variación moderada. La media sigue siendo útil, pero debe leerse junto con su banda de variación.',
    };
  }

  if (mean != null && spread < 0.07) {
    return {
      label: 'Estable',
      tone: 'success',
      narrative:
        'El rendimiento histórico muestra una conversión relativamente consistente y ofrece una base sólida para explicar el comportamiento observado del periodo.',
    };
  }

  return {
    label: 'Con lectura parcial',
    tone: 'default',
    narrative:
      'La información disponible permite una lectura orientativa del rendimiento, aunque todavía no justifica una conclusión ejecutiva fuerte.',
  };
}

function bestSupplier(suppliers: NonNullable<YieldSummaryResponse['suppliers']>) {
  return [...suppliers]
    .filter((item) => (item.yield_mean || 0) > 0)
    .sort((a, b) => (b.yield_mean || 0) - (a.yield_mean || 0))[0];
}

function mostStableSupplier(suppliers: NonNullable<YieldSummaryResponse['suppliers']>) {
  return [...suppliers]
    .filter((item) => item.yield_range != null)
    .sort((a, b) => (a.yield_range || 0) - (b.yield_range || 0))[0];
}

function mainSupplierByVolume(suppliers: NonNullable<YieldSummaryResponse['suppliers']>) {
  return [...suppliers].sort((a, b) => (b.total_kg || 0) - (a.total_kg || 0))[0];
}

export default function YieldView() {
  const [raw, setRaw] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const data = useMemo(() => normalizeResponse(raw), [raw]);
  const global = data.global;
  const suppliers = data.suppliers || [];
  const state = useMemo(() => computeState(data), [data]);
  const topYieldSupplier = useMemo(() => bestSupplier(suppliers), [suppliers]);
  const stableSupplier = useMemo(() => mostStableSupplier(suppliers), [suppliers]);
  const topVolumeSupplier = useMemo(() => mainSupplierByVolume(suppliers), [suppliers]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getIndustrialRiskYieldSummary({
          period_key: '2025',
          max_suppliers: 8,
        });
        if (!cancelled) setRaw(response);
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || e?.message || 'No se pudo cargar la lectura de rendimiento.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Stack spacing={2.5}>
      <Box
        sx={{
          p: 2.4,
          borderRadius: 4,
          bgcolor: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Stack spacing={1.1}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={1.2}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: -0.6 }}>
                Rendimiento
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.82, mt: 0.5 }}>
                Lectura ejecutiva de cómo se convirtió realmente la materia prima en producto útil y cuánto varió esa conversión.
              </Typography>
            </Box>

            <Chip label={state.label} color={state.tone} variant="filled" sx={{ fontWeight: 800 }} />
          </Stack>

          <Typography variant="body1" sx={{ opacity: 0.92 }}>
            {state.narrative}
          </Typography>
        </Stack>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress color="inherit" />
        </Box>
      ) : null}

      {!loading && !error ? (
        <>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6} lg={3}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  height: '100%',
                }}
              >
                <Typography variant="body2" sx={{ opacity: 0.72 }}>
                  Conversión media del periodo
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.6 }}>
                  {pct01(global?.yield_mean, 1)}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.6 }}>
                  Qué parte de la materia prima terminó convertida en producto útil.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  height: '100%',
                }}
              >
                <Typography variant="body2" sx={{ opacity: 0.72 }}>
                  Banda habitual de rendimiento
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.6 }}>
                  {pct01(global?.yield_p10, 1)} – {pct01(global?.yield_p90, 1)}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.6 }}>
                  Rango en el que se movió normalmente la conversión observada.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  height: '100%',
                }}
              >
                <Typography variant="body2" sx={{ opacity: 0.72 }}>
                  Mejor conversión observada
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 900, mt: 0.6 }}>
                  {global?.best_supplier_by_yield_mean || topYieldSupplier?.supplier || '—'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.6 }}>
                  Proveedor con mayor rendimiento medio dentro de la base analizada.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  height: '100%',
                }}
              >
                <Typography variant="body2" sx={{ opacity: 0.72 }}>
                  Mayor consistencia
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 900, mt: 0.6 }}>
                  {global?.most_stable_supplier || stableSupplier?.supplier || '—'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.6 }}>
                  Proveedor con menor dispersión en el rendimiento observado.
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Grid container spacing={1.5}>
            <Grid item xs={12} lg={7}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>
                      Proveedores principales
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.78, mt: 0.4 }}>
                      Cómo cambia el rendimiento observado entre proveedores relevantes del periodo.
                    </Typography>
                  </Box>

                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }}>
                          Proveedor
                        </TableCell>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }} align="right">
                          Kg comprados
                        </TableCell>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }} align="right">
                          Lotes
                        </TableCell>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }} align="right">
                          Rend. medio
                        </TableCell>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }} align="right">
                          Banda
                        </TableCell>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }} align="right">
                          Estabilidad
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {suppliers.slice(0, 8).map((row) => (
                        <TableRow key={row.supplier || 'yield-row'}>
                          <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: '#fff', fontWeight: 700 }}>
                            {row.supplier || '—'}
                          </TableCell>
                          <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                            {fmtNumber(row.total_kg)}
                          </TableCell>
                          <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                            {fmtNumber(row.n_lots)}
                          </TableCell>
                          <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                            {pct01(row.yield_mean, 1)}
                          </TableCell>
                          <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                            {pct01(row.yield_p10, 1)} – {pct01(row.yield_p90, 1)}
                          </TableCell>
                          <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                            {row.stability_label || '—'}
                          </TableCell>
                        </TableRow>
                      ))}

                      {!suppliers.length ? (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.7)' }}>
                            No hay base suficiente para construir la lectura por proveedor en este periodo.
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </TableBody>
                  </Table>
                </Stack>
              </Box>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  height: '100%',
                }}
              >
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>
                      Lectura ejecutiva
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.78, mt: 0.4 }}>
                      Qué dice el histórico sobre la conversión real de materia prima y por qué la media anual no describe todos los lotes por igual.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2.5,
                      bgcolor: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.06)',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Mensaje principal
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.84, mt: 0.7 }}>
                      {state.narrative}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2.5,
                      bgcolor: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.06)',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Qué explica el año
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.84, mt: 0.7 }}>
                      {topVolumeSupplier?.supplier
                        ? `Una parte relevante de la lectura del periodo viene marcada por ${topVolumeSupplier.supplier}, al concentrar el mayor volumen analizado.`
                        : 'Todavía no hay base suficiente para identificar qué proveedor condicionó más la lectura del periodo.'}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2.5,
                      bgcolor: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.06)',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Implicación para dirección
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.84, mt: 0.7 }}>
                      {stableSupplier?.supplier
                        ? `No basta con mirar una media anual: la diferencia entre proveedores y la consistencia de ${stableSupplier.supplier} ayudan a entender qué parte del rendimiento es estructural y qué parte responde al mix real del periodo.`
                        : 'La media anual debe leerse junto con la variación entre lotes para evitar conclusiones demasiado simples sobre el rendimiento real.'}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2.5,
                      bgcolor: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.06)',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Importante
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.84, mt: 0.7 }}>
                      {data.quality_report?.note ||
                        'Entorno de prueba: lectura histórica orientativa. No equivale a un rendimiento oficial cerrado.'}
                    </Typography>
                  </Box>

                  {(data.quality_report?.reasons || []).length ? (
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2.5,
                        bgcolor: 'rgba(255,255,255,0.03)',
                        border: '1px solid rgba(255,255,255,0.06)',
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        Observaciones del periodo
                      </Typography>
                      <Stack spacing={0.8} sx={{ mt: 1 }}>
                        {(data.quality_report?.reasons || []).map((reason: string) => (
                          <Typography key={reason} variant="body2" sx={{ opacity: 0.82 }}>
                            • {reason}
                          </Typography>
                        ))}
                      </Stack>
                    </Box>
                  ) : null}
                </Stack>
              </Box>
            </Grid>
          </Grid>

          <Typography variant="caption" sx={{ opacity: 0.65 }}>
            Entorno de trabajo orientado a lectura ejecutiva. Esta pantalla muestra una lectura histórica del rendimiento observado y no una cifra contable u oficial cerrada.
          </Typography>
        </>
      ) : null}
    </Stack>
  );
}